import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { Col, Container, Row } from "react-bootstrap";

import { Dialog } from "primereact/dialog";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";

import { MaximumFileSizeBytes, ResultFileUpload } from "../shared/interfaces/Result";
import { ProgressButton } from "../shared/ProgressButton";
import { DropzoneUploader } from "../shared/DropzoneUploader";
import { Card } from "../shared/Card";
import { FormContainer, FormItem } from "../shared/FormLayout/FormContainer";
import { HVSubstationDropdown } from "../HVSubstationDropdown";
import { Customer } from "../shared/interfaces/Customer";

import { useResultFileUpload } from "../data/useResultFileUpload";

import { MessageToast, ToastMode } from "../shared/MessageToast";
import { CancelButton } from "../shared/CancelButton";
import { CustomerDropdown } from "../CustomerDropdown";
import { CompanyDropdown } from "../CompanyDropdown";
import { SiteDropdown } from "../SiteDropdown";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { useCompaniesByCustomerId } from "../data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../data/useSitesByCompanyId";

interface Props {
  selectedCustomer?: Customer;
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  onCustomerSelect: (customer: Customer | undefined) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    centralComponent: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "20px",
    },
    cancelButton: {
      textAlign: "right",
      padding: "20px",
    },
    customerName: {
      color: "#00AAe4",
    },
    switchCustomer: {
      cursor: "pointer",
      color: "#00AAe4",
    },
  }),
);

export function ResultUploader(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const { selectedCustomer, onUploadSuccess, onCustomerSelect } = props;

  const [resultFile, setResultFile] = useState<ResultFileUpload | undefined>();
  const [updateSwitchgearSelection, setUpdateSwitchgearSelection] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const resultFileUpload = useResultFileUpload();
  const [customerDropdownShowing, setCustomerDropdownShowing] = useState(false);

  const altRegionNames = useAlternativeRegionNames(selectedCustomer?.id);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  const companies = useCompaniesByCustomerId(selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies, selectedCompanyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && selectedSiteName) {
      return sites.data?.find((c) => c.name === selectedSiteName)?.id;
    }
  }, [sites.data, selectedSiteName]);

  const handleFileSelection = useCallback(
    async (file: File[]) => {
      if (file && file.length > 0) {
        setResultFile({
          ResultFile: file[0],
          CustomerId: selectedCustomer?.id,
        });
      }
    },
    [selectedCustomer],
  );

  const handleCustomerChange = useCallback(
    (customer?: Customer) => {
      onCustomerSelect(customer);
      resultFile &&
        setResultFile({
          ...resultFile,
          HVSubstationId: undefined,
          SwitchGearId: undefined,
        });
    },
    [resultFile, onCustomerSelect],
  );

  const handleCompanyChange = useCallback(
    (selectedCompanyName: string) => {
      setSelectedCompanyName(selectedCompanyName);
      setSelectedSiteName(undefined);
    },
    [setSelectedCompanyName, setSelectedSiteName],
  );

  const handleSiteChange = useCallback(
    (selectedSiteName: string) => {
      setSelectedSiteName(selectedSiteName);
    },
    [setSelectedSiteName],
  );

  const handleSubstationChange = useCallback(
    (hvSubstationId?: number) => {
      resultFile &&
        selectedCustomer &&
        setResultFile({
          ...resultFile,
          CustomerId: selectedCustomer.id,
          HVSubstationId: hvSubstationId,
          SwitchGearId: undefined,
        });
    },
    [resultFile, selectedCustomer],
  );

  const handleCancelSwitchgearSelection = useCallback(() => {
    setUpdateSwitchgearSelection(false);
    resultFile &&
      selectedCustomer &&
      setResultFile({
        ...resultFile,
        CustomerId: selectedCustomer.id,
        HVSubstationId: undefined,
        SwitchGearId: undefined,
      });
  }, [resultFile, selectedCustomer]);

  useEffect(() => {
    if (resultFileUpload.isError) {
      if (resultFileUpload.error && (resultFileUpload.error as Response).status === 415) {
        setErrorMessage(t("fileUpload.fileInvalidType"));
      } else {
        setErrorMessage(t("fileUpload.uploadError"));
      }
    } else {
      setErrorMessage("");
    }
  }, [resultFileUpload.isError, resultFileUpload.error, t]);

  useEffect(() => {
    if (resultFileUpload.isSuccess) {
      resultFileUpload.reset();
      setResultFile(undefined);
      onUploadSuccess();
    }
  }, [resultFileUpload.isSuccess, onUploadSuccess, resultFileUpload]);

  const handleUploadButtonClick = useCallback(() => {
    resultFile && resultFileUpload.mutate(resultFile);
  }, [resultFile, resultFileUpload]);

  const handleCloseButtonClick = useCallback(() => {
    setCustomerDropdownShowing(false);
    setResultFile(undefined);
    resultFileUpload.reset();
    props.onClose();
  }, [props, resultFileUpload]);

  const optionalResultDetails: FormItem[] = [
    { label: t("customer.customer"), value: selectedCustomer?.name },
    {
      label: altRegionNames.data?.companyAltName ?? t("common.company"),
      value: (
        <CompanyDropdown
          customerId={selectedCustomer?.id}
          selectedCompanyName={selectedCompanyName}
          onCompanyChange={handleCompanyChange}
          showClear={true}
        />
      ),
    },
    {
      label: altRegionNames.data?.siteAltName ?? t("common.site"),
      value: (
        <SiteDropdown
          companyId={companyId}
          customerId={selectedCustomer?.id}
          selectedSiteName={selectedSiteName}
          onSiteChange={handleSiteChange}
          showClear={true}
        />
      ),
    },
    {
      label: t("substations.hvSubstation"),
      value: (
        <HVSubstationDropdown
          key="dropdown-substation-id"
          selectedSubstationId={resultFile?.HVSubstationId}
          customerId={selectedCustomer?.id}
          companyId={companyId}
          siteId={siteId}
          name={"substationId"}
          onSubstationChange={handleSubstationChange}
          disabled={!selectedCustomer}
        />
      ),
      optional: true,
    },
  ];

  const header = (
    <Typography variant="h5" component="p">
      {t("results.submitResult")}
    </Typography>
  );
  const footer = (
    <div className="d-flex flex-row align-items-center justify-content-end">
      <MessageToast text={errorMessage} mode={ToastMode.Error} show={resultFileUpload.isError} />
      <ProgressButton
        onClick={handleUploadButtonClick}
        disabled={resultFileUpload.isLoading}
        loading={resultFileUpload.isLoading}
        startIcon={<FontAwesomeIcon style={{ cursor: "pointer" }} icon={faUpload} />}
      >
        {t("results.submitResult")}
      </ProgressButton>
      <CancelButton onClick={handleCloseButtonClick} disabled={resultFileUpload.isLoading} className="m1-1" />
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={props.open}
      onHide={handleCloseButtonClick}
      breakpoints={{ "960px": "75vw", "1920px": "85vw" }}
      style={{ width: "65vw" }}
      footer={footer}
    >
      {selectedCustomer && (
        <div>
          Submit a new <span className={classes.customerName}>{selectedCustomer.name}</span> result
          <br />
          {!customerDropdownShowing && (
            <>
              <span className={classes.switchCustomer} onClick={() => setCustomerDropdownShowing(true)}>
                Switch customer
              </span>
              <br />
              <br />
            </>
          )}
        </div>
      )}

      {customerDropdownShowing && (
        <>
          <br />
          <CustomerDropdown
            key="dropdown-customer"
            selectedCustomer={selectedCustomer}
            onCustomerSelect={handleCustomerChange}
            onSingleCustomerAvailable={(selectedCustomer) => onCustomerSelect(selectedCustomer)}
            displaySingleCustomerName
            showLabel
          />
          <br />
        </>
      )}

      <DropzoneUploader
        acceptedMimeTypes="application/zip, application/x-zip-compressed"
        acceptedFileTypeTranslationKeys={["fileTypes.zipExtension"]}
        maxFiles={1}
        maxSize={MaximumFileSizeBytes}
        onFileSelection={handleFileSelection}
      />

      {resultFile && (
        <Container fluid>
          <Row className={classes.centralComponent}>
            <Col xl={6}>
              <Card title={t("results.resultSwitchgearSelection")}>
                <Typography variant="caption" component="p" align="center">
                  {t("results.overrideSwitchgear")}
                </Typography>
                <Typography variant="caption" component="p" align="center">
                  {t("results.skipSwitchgearSelection")}
                </Typography>
                <Typography variant="caption" component="p" align="center">
                  {t("results.panelMatching")}
                </Typography>
                <Typography variant="caption" component="p" align="center">
                  {t("results.ifMatchCannotBeFound")}
                </Typography>

                <div className={classes.centralComponent}>
                  {!updateSwitchgearSelection && (
                    <Button onClick={() => setUpdateSwitchgearSelection(true)} variant="contained" color="primary">
                      {t("switchgear.updateSwitchgear")}
                    </Button>
                  )}
                </div>
                {updateSwitchgearSelection && <FormContainer dataList={optionalResultDetails} />}
                <div className={classes.cancelButton}>
                  {updateSwitchgearSelection && (
                    <CancelButton onClick={handleCancelSwitchgearSelection} className="m1-1" />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </Dialog>
  );
}
